'use client';

import { useId, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { cn } from '@/lib/utils/cn';

/**
 * Form controls.
 *
 * 40px tall, 8px corner — the same corner as a Button, so a field and the
 * control that submits it read as the same family. (At 44/10 the field was a
 * step larger than everything around it and a six-field form ran off the fold.)
 *
 * The resting border is the light hairline; it steps up to `line-strong` on
 * hover. That two-stage treatment is what makes an idle form look calm and a
 * hovered field look live, without either state shouting.
 *
 * Focus is a brand border plus a soft outer ring — legible on white and on
 * tinted surfaces alike, and the one place brand colour is load-bearing now
 * that the primary button is black.
 */

/** Shared control chrome — Input, Select and Textarea must not drift apart. */
export const controlClass = cn(
  'w-full rounded-md border border-line-strong bg-surface text-sm text-ink shadow-xs',
  // `muted`, not `muted-soft`. A placeholder is text and has to be readable:
  // muted-soft measures 3.40:1 on white, under AA. Real values are `text-ink`
  // at 18.5:1, so a placeholder at 5.17:1 is still plainly the lighter of the
  // two — the distinction never needed a colour that could not be read.
  'placeholder:text-muted',
  'transition-[border-color,box-shadow] duration-[--duration-fast] ease-[--ease-out-quart]',
  'hover:border-muted-soft',
  'focus:border-brand focus:ring-[3px] focus:ring-brand/15 focus:outline-none',
  'disabled:cursor-not-allowed disabled:bg-canvas-alt disabled:text-muted'
);

const INVALID = 'border-danger hover:border-danger focus:border-danger focus:ring-danger/15';

/**
 * Label + control + hint/error wrapper. Split out from Input so Select,
 * Textarea and any bespoke control get the same label typography, the same
 * gap, and the same `aria-describedby` wiring.
 */
export function Field({ label, hint, error, htmlFor, required, children, className }) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {label && (
        <label htmlFor={htmlFor} className="text-label font-medium text-ink">
          {label}
          {required && (
            <span className="ml-0.5 text-danger" aria-hidden="true">
              *
            </span>
          )}
        </label>
      )}
      {children}
      {error ? (
        <span id={htmlFor && `${htmlFor}-msg`} className="text-xs font-medium text-danger" role="alert">
          {error}
        </span>
      ) : hint ? (
        <span id={htmlFor && `${htmlFor}-msg`} className="text-xs text-muted">
          {hint}
        </span>
      ) : null}
    </div>
  );
}

export default function Input({
  label,
  hint,
  error,
  className,
  id,
  required,
  showPasswordToggle = false,
  leadingIcon: LeadingIcon,
  suffix,
  type = 'text',
  ...props
}) {
  const generatedId = useId();
  const inputId = id || props.name || generatedId;
  const [visible, setVisible] = useState(false);

  const isPassword = showPasswordToggle && type === 'password';
  const inputType = isPassword && visible ? 'text' : type;

  return (
    <Field label={label} hint={hint} error={error} htmlFor={inputId} required={required} className={className}>
      <div className="relative">
        {LeadingIcon && (
          <LeadingIcon
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-soft"
            aria-hidden="true"
          />
        )}
        <input
          id={inputId}
          type={inputType}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={hint || error ? `${inputId}-msg` : undefined}
          className={cn(
            controlClass,
            'h-10 px-3',
            LeadingIcon && 'pl-9',
            (isPassword || suffix) && 'pr-10',
            error && INVALID
          )}
          {...props}
        />
        {isPassword ? (
          <button
            type="button"
            className="absolute top-1/2 right-1.5 flex size-7 -translate-y-1/2 items-center justify-center rounded-md text-muted transition-colors hover:bg-canvas-alt hover:text-ink"
            onClick={() => setVisible((current) => !current)}
            aria-label={visible ? 'Hide password' : 'Show password'}
          >
            {visible ? (
              <EyeOff className="size-4" aria-hidden="true" />
            ) : (
              <Eye className="size-4" aria-hidden="true" />
            )}
          </button>
        ) : suffix ? (
          <span className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-sm text-muted">
            {suffix}
          </span>
        ) : null}
      </div>
    </Field>
  );
}

export function Textarea({ label, hint, error, className, id, required, rows = 4, ...props }) {
  const generatedId = useId();
  const inputId = id || props.name || generatedId;

  return (
    <Field label={label} hint={hint} error={error} htmlFor={inputId} required={required} className={className}>
      <textarea
        id={inputId}
        rows={rows}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={hint || error ? `${inputId}-msg` : undefined}
        className={cn(controlClass, 'resize-y px-3 py-2.5 leading-6', error && INVALID)}
        {...props}
      />
    </Field>
  );
}

export function Select({ label, hint, error, className, id, required, children, ...props }) {
  const generatedId = useId();
  const inputId = id || props.name || generatedId;

  return (
    <Field label={label} hint={hint} error={error} htmlFor={inputId} required={required} className={className}>
      <div className="relative">
        <select
          id={inputId}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={hint || error ? `${inputId}-msg` : undefined}
          className={cn(controlClass, 'h-10 appearance-none pr-9 pl-3', error && INVALID)}
          {...props}
        >
          {children}
        </select>
        {/* Inline chevron rather than a lucide import — this file is otherwise
            the only reason Select would need one, and the native arrow is
            suppressed by `appearance-none`. */}
        <svg
          className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-muted"
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden="true"
        >
          <path d="m4 6 4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </Field>
  );
}
